import clsx from "clsx";
import { motion } from "framer-motion";
import { ALL_USER_ICONS } from "../../helpers/user-icons";
import Icon from "../Icon/Icon";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { useTranslation } from "react-i18next";
import { usePagination } from "src/hooks/usePagination";
import useMobile from "src/hooks/useMobile";
import CardsPagination from "../Cards/CardsPagination";
import { useEffect, useMemo } from "react";
import { useRafState } from "react-use";
import TableQuickFilter from "../TableQuickFilter/TableQuickFilter";
import { ICON_SIZES } from "src/constants/icons";
import { IIconSelector } from "./IconSelector.types";

const MOBILE_PAGE_SIZE = 16;
const DESKTOP_PAGE_SIZE = MOBILE_PAGE_SIZE;

const IconSelector: React.FC<IIconSelector> = ({
  onClick,
  selected,
  background,
  color,
  icons = ALL_USER_ICONS,
  mobileColumns = 4,
  desktopColumns = 7,
}) => {
  const [searchText, setSearchText] = useRafState<string>("");
  const defaultBg = true ? "#22d3ee" : "#f87171";
  const defaultColor = true ? "white" : "white";
  const { isMobile } = useMobile();
  const { t } = useTranslation();

  const filteredIcons = useMemo(() => {
    const search = searchText?.trim?.().toLowerCase();

    if (!search) return icons;

    return icons.filter((icon: string) => icon.toLowerCase().includes(search));
  }, [searchText, icons]);

  const {
    items,
    canNext,
    next,
    prev,
    canPrev,
    totalPages,
    page,
    goTo,
    showPagination,
  } = usePagination(filteredIcons, {
    pageSize: isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE,
  });

  useEffect(() => {
    const activeIndex = icons.findIndex((item) => item === selected);

    if (activeIndex === -1) return;

    const targetPage =
      Math.floor(
        activeIndex / (isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE),
      ) + 1;

    if (page !== targetPage && targetPage <= totalPages) {
      goTo(targetPage);
    }
  }, [selected, isMobile, goTo, totalPages]);

  return (
    <CollapsibleBlock title={t("misc:strings.icon")} blocked open>
      <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
        <div className={clsx(["flex", "w-full", "pb-4"])}>
          <TableQuickFilter
            searchTerm={searchText}
            onChange={(value: string) => {
              setSearchText(value);
              goTo(1);
            }}
            placeholder="Buscar..."
          />
        </div>
        <div
          className={clsx([
            "grid",
            `grid-cols-${mobileColumns}`,
            `md:grid-cols-${desktopColumns}`,
            "gap-2",
          ])}
        >
          {items?.map?.((icon: string) => {
            return (
              <motion.div
                key={icon}
                className={clsx([
                  "flex",
                  "flex-col",
                  "items-center",
                  "justify-center",
                  "cursor-pointer",
                  "truncate",
                  "rounded",
                  "h-[70px]",
                  "transition-colors",
                  "p-2",
                  {
                    "text-gray-400": selected !== icon,
                    "hover:text-gray-500 hover:bg-gray-200": selected !== icon,
                  },
                ])}
                style={{
                  ...(selected === icon
                    ? background
                      ? { background }
                      : { background: defaultBg }
                    : {}),
                  ...(selected === icon
                    ? color
                      ? { color }
                      : { color: defaultColor }
                    : {}),
                }}
                onClick={() => {
                  if (selected === icon) {
                    onClick("");
                  } else {
                    onClick(icon);
                  }
                }}
                id={icon}
                whileTap={{ scale: 0.9 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 17,
                  duration: 0.3,
                }}
              >
                <Icon name={icon as any} size={ICON_SIZES.LG} />
                <div
                  className={clsx(["text-[11px]", "truncate", "max-w-full"])}
                >
                  {icon.substring(2)}
                </div>
              </motion.div>
            );
          }) || ""}
        </div>
        {showPagination && (
          <CardsPagination
            canNext={canNext}
            canPrev={canPrev}
            goTo={goTo}
            next={next}
            prev={prev}
            page={page}
            totalPages={totalPages}
            totalItems={filteredIcons?.length}
            pageSize={isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE}
          />
        )}
      </div>
    </CollapsibleBlock>
  );
};

export default IconSelector;
